/*
 * alyqra.js — interactive web components for the Alyqra Dot landing page.
 * Vanilla JS, no dependencies. Loaded once with `defer`.
 *
 * Custom elements:
 *   <alyqra-gallery>      Keyboard-operable product image gallery.
 *   <alyqra-accordion>    Independent expand/collapse panels (button + aria-expanded).
 *   <alyqra-faq>          Category tablist + filterable accordion items.
 *   <alyqra-countdown>    Fixed-date timer; honours prefers-reduced-motion.
 *   <alyqra-sticky-atc>   Scroll-triggered sticky buy bar.
 *   <alyqra-product-form> Progressive-enhancement Cart API add-to-cart.
 *
 * Cross-component events are dispatched on `document` under the `alyqra:` and
 * Shopify-standard (`cart:*`) namespaces so other theme components can react.
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------------
   * <alyqra-gallery>
   * Markup: a [data-main] <img>, and [data-thumb] <button> elements each with a
   * data-full (large src) and optional data-alt.
   * ------------------------------------------------------------------------- */
  if (!customElements.get('alyqra-gallery')) {
    customElements.define('alyqra-gallery', class extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-main]');
        this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));
        if (!this.main || !this.thumbs.length) return;
        this.thumbs.forEach(function (thumb, i) {
          thumb.addEventListener('click', this.select.bind(this, i));
          thumb.addEventListener('keydown', this.onKeydown.bind(this, i));
        }, this);
      }
      select(index) {
        var thumb = this.thumbs[index];
        if (!thumb) return;
        var full = thumb.getAttribute('data-full');
        if (full) this.main.src = full;
        var alt = thumb.getAttribute('data-alt');
        if (alt !== null) this.main.alt = alt;
        this.thumbs.forEach(function (t) {
          t.classList.remove('is-active');
          t.removeAttribute('aria-current');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-current', 'true');
      }
      onKeydown(index, event) {
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.thumbs.length - 1;
        if (next === null) return;
        event.preventDefault();
        next = (next + this.thumbs.length) % this.thumbs.length;
        this.thumbs[next].focus();
        this.select(next);
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * <alyqra-accordion>
   * Markup: repeated [data-acc-item] each containing a <button data-acc-trigger
   * aria-expanded aria-controls="<id>"> and a panel #<id>[data-acc-panel].
   * ------------------------------------------------------------------------- */
  if (!customElements.get('alyqra-accordion')) {
    customElements.define('alyqra-accordion', class extends HTMLElement {
      connectedCallback() {
        this.single = this.hasAttribute('data-single');
        this.triggers = Array.prototype.slice.call(this.querySelectorAll('[data-acc-trigger]'));
        this.triggers.forEach(function (trigger) {
          trigger.addEventListener('click', this.toggle.bind(this, trigger));
        }, this);
      }
      toggle(trigger) {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        if (this.single && !expanded) {
          this.triggers.forEach(function (t) { this.setState(t, false); }, this);
        }
        this.setState(trigger, !expanded);
      }
      setState(trigger, open) {
        trigger.setAttribute('aria-expanded', String(open));
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        var item = trigger.closest('[data-acc-item]');
        if (item) item.classList.toggle('is-open', open);
        if (panel) {
          if (open) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        }
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * <alyqra-faq>
   * Markup: a [role="tablist"] of <button role="tab" data-faq-cat="key">, plus
   * [data-faq-item][data-cat] accordion items. "all" shows everything.
   * ------------------------------------------------------------------------- */
  if (!customElements.get('alyqra-faq')) {
    customElements.define('alyqra-faq', class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
        this.items = Array.prototype.slice.call(this.querySelectorAll('[data-faq-item]'));
        this.tabs.forEach(function (tab, i) {
          tab.addEventListener('click', this.activate.bind(this, i));
          tab.addEventListener('keydown', this.onKeydown.bind(this, i));
        }, this);
      }
      activate(index) {
        var tab = this.tabs[index];
        var cat = tab.getAttribute('data-faq-cat');
        this.tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
        });
        this.items.forEach(function (item) {
          var show = cat === 'all' || item.getAttribute('data-cat') === cat;
          item.hidden = !show;
        });
      }
      onKeydown(index, event) {
        var next = null;
        if (event.key === 'ArrowRight') next = index + 1;
        else if (event.key === 'ArrowLeft') next = index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        next = (next + this.tabs.length) % this.tabs.length;
        this.tabs[next].focus();
        this.activate(next);
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * <alyqra-countdown data-end="ISO" data-ended-label="...">
   * ------------------------------------------------------------------------- */
  if (!customElements.get('alyqra-countdown')) {
    customElements.define('alyqra-countdown', class extends HTMLElement {
      connectedCallback() {
        var end = this.getAttribute('data-end');
        this.deadline = end ? new Date(end).getTime() : NaN;
        if (isNaN(this.deadline)) return;
        this.units = {
          days: this.querySelector('[data-unit="days"]'),
          hours: this.querySelector('[data-unit="hours"]'),
          minutes: this.querySelector('[data-unit="minutes"]'),
          seconds: this.querySelector('[data-unit="seconds"]')
        };
        this.render();
        // Reduced motion: update once a minute (still informative, no second-by-second flicker).
        this.timer = setInterval(this.render.bind(this), REDUCED_MOTION ? 60000 : 1000);
      }
      disconnectedCallback() { if (this.timer) clearInterval(this.timer); }
      pad(n) { return String(n).padStart(2, '0'); }
      render() {
        var diff = Math.max(0, this.deadline - Date.now());
        if (diff === 0) {
          if (this.timer) clearInterval(this.timer);
          var label = this.getAttribute('data-ended-label');
          if (label) {
            var grid = this.querySelector('.alyqra-countdown__grid');
            if (grid) grid.textContent = label;
          }
          return;
        }
        var s = Math.floor(diff / 1000);
        var d = Math.floor(s / 86400); s -= d * 86400;
        var h = Math.floor(s / 3600); s -= h * 3600;
        var m = Math.floor(s / 60); s -= m * 60;
        if (this.units.days) this.units.days.textContent = this.pad(d);
        if (this.units.hours) this.units.hours.textContent = this.pad(h);
        if (this.units.minutes) this.units.minutes.textContent = this.pad(m);
        if (this.units.seconds) this.units.seconds.textContent = this.pad(s);
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * <alyqra-sticky-atc data-threshold="900">
   * ------------------------------------------------------------------------- */
  if (!customElements.get('alyqra-sticky-atc')) {
    customElements.define('alyqra-sticky-atc', class extends HTMLElement {
      connectedCallback() {
        this.threshold = parseInt(this.getAttribute('data-threshold'), 10) || 900;
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
      disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(function () {
          this.classList.toggle('is-visible', window.scrollY > this.threshold);
          this.ticking = false;
        }.bind(this));
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * <alyqra-product-form>
   * Progressive enhancement: intercepts the standard product form, adds via the
   * Cart API, dispatches cart events, and shows inline feedback. With JS off the
   * form posts to /cart as normal.
   * ------------------------------------------------------------------------- */
  if (!customElements.get('alyqra-product-form')) {
    customElements.define('alyqra-product-form', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.button = this.form.querySelector('[type="submit"]');
        this.feedback = this.querySelector('[data-atc-feedback]');
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
      onSubmit(event) {
        if (!window.fetch) return; // graceful fallback to normal POST
        event.preventDefault();
        var data = new FormData(this.form);
        this.setLoading(true);
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: data
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (e) { throw e; });
            return res.json();
          })
          .then(function (item) {
            this.announce(this.getAttribute('data-added-label') || 'Added to cart');
            document.dispatchEvent(new CustomEvent('alyqra:cart:added', { bubbles: true, detail: { item: item } }));
            // Common Shopify theme cart-refresh hooks:
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { item: item } }));
          }.bind(this))
          .catch(function (err) {
            var msg = (err && (err.description || err.message)) || this.getAttribute('data-error-label') || 'Could not add to cart';
            this.announce(msg, true);
          }.bind(this))
          .finally(function () { this.setLoading(false); }.bind(this));
      }
      setLoading(loading) {
        if (!this.button) return;
        this.button.toggleAttribute('aria-busy', loading);
        this.button.disabled = loading;
      }
      announce(message, isError) {
        if (!this.feedback) return;
        this.feedback.textContent = message;
        this.feedback.classList.toggle('is-error', !!isError);
      }
    });
  }
})();
